import { cn } from '@/lib/helpers/utils';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import { Image, ImageTypes } from '../../../types/content';
import { ConditionalWrapper } from './ConditionalWrapper';

type ATFImageProps = {
  imageData: any;
  alt: string;
  eager?: boolean;
  containerClass?: string;
  imageClass?: string;
  imageStyle?: any;
};

/**
 * ATFImage - Above the fold image, used to render images in the viewport to speed up LCP on load
 * ** Should not be used all over the site.
 * @param props <any>
 * @returns
 */
export const ATFImage = ({ imageData, alt, eager, containerClass, imageClass, imageStyle }: ATFImageProps) => {
  const condition = containerClass ? true : false;
  return (
    imageData?.images && (
      <ConditionalWrapper
        condition={condition}
        wrapper={(children: any) => <div className={containerClass}>{children}</div>}
      >
        <picture>
          {imageData?.images.sources &&
            imageData.images.sources.map((source: any, index: number) => {
              return (
                <source key={`source_image_${index}`} sizes={source.sizes} srcSet={source.srcSet} type={source.type} />
              );
            })}
          {imageData?.images.fallback && (
            <img
              alt={alt}
              style={imageStyle ? imageStyle : {}}
              className={cn(imageClass ? imageClass : '')}
              loading={eager ? 'eager' : 'lazy'}
              src={imageData?.images.fallback.src}
              srcSet={imageData?.images.fallback.srcSet}
              sizes={imageData?.images.fallback.sizes}
            />
          )}
        </picture>
      </ConditionalWrapper>
    )
  );
};

export interface ImageTypeToImageProps extends React.HTMLAttributes<HTMLDivElement> {
  image: Image;
  imgClassName?: string;
  children?: React.ReactNode;
}

export const ImageTypeToImage = ({ image, className, imgClassName, children }: ImageTypeToImageProps) => {
  const imageData: Image = !image?.type
    ? {
        path: image?.path ? process.env.GATSBY_IMAGE_BASE_URL + image?.path : '',
        alt: image?.alt ?? '',
        type: ImageTypes.img
      }
    : image;
  const regexChecker = /.*\/(null|undefined|)$/gm;
  switch (imageData.type) {
    case ImageTypes.gatsby:
      if (!imageData.image) {
        return children ? children : null;
      }
      return (
        imageData.image?.images?.fallback &&
        imageData.image?.images?.fallback?.src &&
        !(imageData.image?.images?.fallback?.src as string).match(regexChecker) && (
          <GatsbyImage
            className={className}
            imgClassName={imgClassName}
            image={imageData.image}
            alt={imageData.alt ?? ''}
          />
        )
      );
    case ImageTypes.img:
      if (!imageData.path) {
        return children ? children : null;
      }
      return (
        !(imageData.path as string).match(regexChecker) && (
          <img src={imageData.path} alt={imageData.alt} loading="lazy" className={cn(className, imgClassName)} />
        )
      );
    case ImageTypes.html:
      return <div className={cn('relative', className, imgClassName)}>{imageData.image}</div>;
  }
};
